import React, { useMemo, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Pagination from 'components_new/atoms/Pagination'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleTable from 'components_new/molecules/SimpleTable'

import { JobSummary } from 'redux/selectors/internal/DataRuns'
import {
  formatClock,
  formatDayClock,
  formatDurationMin,
  STATUS_COLOR,
  STATUS_LABEL,
  STREAK_HIGHLIGHT_THRESHOLD
} from '../utils'

const PAGE_SIZE = 20

export interface DataRunsFailListProps {
  jobs: JobSummary[]
  onSelectJobRun: (job: JobSummary) => void
}

const COLUMNS = [
  { title: 'Status', width: 90 },
  { title: 'Jobb' },
  { title: 'Kund' },
  { title: 'Start', align: 'right' as const, width: 64 },
  { title: 'Längd', align: 'right' as const, width: 76 },
  { title: 'I rad', align: 'center' as const, width: 56 },
  { title: 'Senast lyckad', align: 'right' as const, width: 120 },
  { title: '', width: 24 }
]

/**
 * "Fel i senaste körningen" - jobs currently failing or timed out, sorted
 * by consecutive-failure streak (longest-running problem first). Uses
 * SimpleTable for the body/loading/hover behavior and its own pagination
 * on top, since the sort order here is fixed rather than user-driven.
 */
const DataRunsFailList = (props: DataRunsFailListProps) => {
  const { jobs, onSelectJobRun } = props

  const [page, setPage] = useState(1)

  const sortedJobs = useMemo(
    () =>
      [...jobs].sort((a, b) => b.consecutive_failures - a.consecutive_failures),
    [jobs]
  )

  const pageCount = Math.max(1, Math.ceil(sortedJobs.length / PAGE_SIZE))
  const pagedJobs = sortedJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '20px',
        p: '20px 22px',

        // Column widths here are narrower than the header labels plus the
        // table cell's own padding, so two-word labels ("I rad", "Senast
        // lyckad") would break across two lines.
        '& thead th': { whiteSpace: 'nowrap' }
      }}
    >
      <SectionHeader
        icon={
          <Box
            sx={{
              bgcolor: 'error.main',
              borderRadius: '2px',
              height: '10px',
              width: '10px'
            }}
          />
        }
        title="Fel i senaste körningen"
        titleAdornment={
          <Chip
            color={jobs.length > 0 ? 'error' : 'default'}
            label={String(jobs.length)}
            size="small"
          />
        }
      />

      {jobs.length === 0 ? (
        <EmptyPlaceholder title="Inga fel i senaste körningen." />
      ) : (
        <>
          <SimpleTable
            columns={COLUMNS}
            disableSorting
            rows={pagedJobs.map((job) => ({
              id: `${job.customer?.id ?? 'none'}-${job.job_name}`,
              onClick: () => onSelectJobRun(job),
              cells: [
                <Tag
                  color={STATUS_COLOR[job.status]}
                  key="status"
                  label={STATUS_LABEL[job.status]}
                />,
                <Text
                  key="job"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  variant="body2"
                >
                  {job.job_name}
                </Text>,
                <Text
                  key="customer"
                  sx={{
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  variant="body2"
                >
                  {job.customer?.name ?? '–'}
                </Text>,
                <Text
                  key="start"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                  variant="body2"
                >
                  {formatClock(job.started_at)}
                </Text>,
                <Text
                  key="duration"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                  variant="body2"
                >
                  {formatDurationMin(job.duration_min)}
                </Text>,
                <Box
                  key="streak"
                  sx={{
                    bgcolor:
                      job.consecutive_failures >= STREAK_HIGHLIGHT_THRESHOLD
                        ? 'error.transparent'
                        : undefined,
                    borderRadius: '8px',
                    display: 'inline-block',
                    fontVariantNumeric: 'tabular-nums',
                    px: '6px'
                  }}
                >
                  <Text
                    component="span"
                    sx={{
                      color:
                        job.consecutive_failures >= STREAK_HIGHLIGHT_THRESHOLD
                          ? 'error.text'
                          : 'text.primary',
                      fontWeight: 600
                    }}
                    variant="body2"
                  >
                    {job.consecutive_failures}×
                  </Text>
                </Box>,
                <Text
                  key="last-ok"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                  variant="body2"
                >
                  {formatDayClock(job.last_successful_at)}
                </Text>,
                <Icon
                  key="chevron"
                  fontSize="small"
                  name="ArrowForwardIos"
                  sx={{ color: 'text.disabled' }}
                />
              ]
            }))}
          />
          <Text
            sx={{ color: 'text.disabled', mt: 1 }}
            variant="caption"
            component="div"
          >
            Klicka en rad för felmeddelande, historik och länk till AWS.
          </Text>
          {pageCount > 1 ? (
            <Pagination
              count={pageCount}
              onChange={(_, newPage) => setPage(newPage)}
              page={page}
              shape="rounded"
              sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
              variant="outlined"
            />
          ) : null}
        </>
      )}
    </Box>
  )
}

DataRunsFailList.displayName = 'DataRunsFailList'
export default DataRunsFailList
